import { useCallback, useEffect, useState } from 'react'

const DISMISS_KEY = 'od_install_dismissed'

const standalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true)

const detectIOS = () =>
  typeof navigator !== 'undefined' &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !window.MSStream

// Manages the PWA install lifecycle across Android/desktop (beforeinstallprompt)
// and iOS (no event → manual Add-to-Home-Screen instructions).
const useInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null)
  const [isInstalled, setIsInstalled] = useState(standalone())
  const [dismissed, setDismissed] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem(DISMISS_KEY) === '1'
  )
  const isIOS = detectIOS()

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault()
      setDeferred(e)
    }
    const onInstalled = () => {
      setIsInstalled(true)
      setDeferred(null)
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferred) return null
    deferred.prompt()
    const choice = await deferred.userChoice
    setDeferred(null)
    return choice?.outcome ?? null
  }, [deferred])

  const dismiss = useCallback(() => {
    setDismissed(true)
    try {
      localStorage.setItem(DISMISS_KEY, '1')
    } catch {
      /* ignore storage errors (private mode) */
    }
  }, [])

  // Something to show if: an install event is available, OR iOS (needs manual steps).
  const canInstall = Boolean(deferred) || (isIOS && !isInstalled)

  return { canInstall, promptInstall, isInstalled, isIOS, dismissed, dismiss }
}

export default useInstallPrompt
